import type { TFunction } from 'i18next';
import { ArrowRight, ChevronRight, Search, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { api, type SearchHit, type SearchHitKind } from '../api';
import { Badge, type Tone } from '../components/Badge';
import { Section } from '../components/blocks';
import { DataError } from '../components/DataError';
import { PageHeader, useDocumentTitle } from '../components/PageHeader';
import { EmptyState, Loading } from '../components/States';
import { classifyQuery, type Confidence, type QueryCandidate } from '../lib/classifyQuery';
import { eventTypeLabel, workflowTypeLabel } from '../lib/labels';
import { correlationLink } from '../lib/opsQuery';
import { useApi } from '../lib/useApi';
import { humanize } from './domains/status';

type T = TFunction<'common'>;
type D = TFunction<'domains'>;
type Lookup = (key: string, options?: Record<string, unknown>) => string;

const CONFIDENCE_TONE: Record<Confidence, Tone> = { high: 'ok', medium: 'info', low: 'neutral' };

/** Where each search hit kind opens, and how it reads: always a name, never a bare ID. */
export function hitTarget(hit: SearchHit, t: D): { to: string | null; title: string; subtitle: string; mono?: boolean } {
  const tr = t as unknown as Lookup;
  const id = encodeURIComponent(hit.id);
  const status = (group: string, value: string | undefined) => (value ? tr(`status.${group}.${value}`, { defaultValue: humanize(value) }) : '');
  const subjectType = (v: string | null | undefined) => (v ? tr(`subject.types.${v}`, { defaultValue: humanize(v) }) : '');
  const detail = hit.detail ?? '';
  switch (hit.kind) {
    case 'domain':
      return {
        to: `/domains/${encodeURIComponent(hit.detail ?? hit.label ?? hit.id)}`,
        title: hit.label ?? detail,
        subtitle: [hit.detail && hit.detail !== hit.label ? hit.detail : null, hit.state ? status('lifecycle', hit.state) : null].filter(Boolean).join(' · '),
      };
    case 'domain_event':
      // `id` is the domain; the event matched by request or correlation id.
      return { to: `/domains/${id}/history`, title: eventTypeLabel(hit.label), subtitle: t('search.hit.event', { domain: detail }) };
    case 'registry_transaction':
      return { to: `/registry/transactions/${id}`, title: hit.label ?? '', subtitle: t('search.hit.registryTx', { detail: [detail, status('command', hit.state)].filter(Boolean).join(' · ') }), mono: true };
    case 'workflow':
      return {
        to: `/ops/workflows/${id}`,
        title: workflowTypeLabel(hit.label),
        subtitle: t('search.hit.workflow', { detail: [detail, status('workflow', hit.state)].filter(Boolean).join(' · ') }),
      };
    case 'subject':
      return hit.label
        ? { to: `/subjects/${id}`, title: hit.label, subtitle: t('search.hit.subject', { type: subjectType(hit.detail) }) }
        : { to: `/subjects/${id}`, title: t('common.unnamed'), subtitle: t('search.hit.subjectHidden', { type: subjectType(hit.detail) }) };
    case 'customer':
      return { to: `/customers/${id}`, title: hit.label ?? '', subtitle: t('search.hit.customer', { type: subjectType(hit.detail) }) };
    case 'transfer':
      return {
        to: `/transfers/${id}`,
        title: hit.label ?? '',
        subtitle: t('search.hit.transfer', { direction: tr(`vocab.direction.${detail}`, { defaultValue: detail }), status: status('transfer', hit.state) }),
      };
    case 'registrant_change':
      return { to: `/registrant-changes/${id}`, title: hit.label ?? '', subtitle: t('search.hit.registrantChange', { status: status('transfer', hit.state) }) };
    case 'dossier':
      return { to: `/dossiers/${id}`, title: hit.label ?? '', subtitle: t('search.hit.dossier', { version: detail, status: status('dossier', hit.state) }) };
    case 'contact':
      return {
        to: hit.registry_code ? `/contacts/${encodeURIComponent(hit.registry_code)}/${id}` : null,
        title: hit.label ?? hit.id,
        subtitle: t('search.hit.contact', { registry: detail }),
        mono: true,
      };
    default:
      return { to: null, title: hit.label ?? '', subtitle: detail };
  }
}

/** English strings produced by `classifyQuery` -> translation keys (`searchPage.cls.*` / `searchPage.actions.*`). */
const CLS_TEXT: Record<string, string> = {
  'W3C traceparent': 'cls.traceparent',
  'Trace ID': 'cls.traceId',
  '128-bit W3C trace ID': 'cls.traceIdDetail',
  'Email address': 'cls.email',
  'Customer or contact lookup': 'cls.emailDetail',
  'Domain name': 'cls.domain',
  'Domain name (Unicode IDN)': 'cls.domainIdnUnicode',
  'Domain name (Punycode IDN)': 'cls.domainIdnPunycode',
  'EPP transaction ID (clTRID / svTRID)': 'cls.trid',
  'Client or server transaction identifier from an EPP exchange': 'cls.tridDetail',
  'Registry object ID (ROID)': 'cls.roid',
  'Repository ID assigned by the registry': 'cls.roidDetail',
  'Numeric reference': 'cls.numeric',
  'Order, invoice or customer number': 'cls.numericDetail',
  'Free text': 'cls.freeText',
  'Name search across domains, customers and subjects': 'cls.freeTextDetail',
  'entity, request, correlation, workflow or registry-transaction ID': 'cls.uuidDetail',
  'Open trace': 'actions.openTrace',
  'Search logs by request ID': 'actions.logsByRequest',
  'Open as workflow': 'actions.openWorkflow',
  'Open as registry transaction': 'actions.openRegistryTx',
  Customers: 'actions.customers',
  'Open domain': 'actions.openDomain',
  'Find registry transaction': 'actions.findRegistryTx',
  Domains: 'actions.domains',
  Orders: 'actions.orders',
  Invoices: 'actions.invoices',
};

/** Translates a text produced by the (language-neutral) classifier; unknown text is shown as is. */
function clsText(text: string, t: T): string {
  const tr = t as unknown as Lookup;
  const key = CLS_TEXT[text];
  if (key) return tr(`searchPage.${key}`);
  const uuid = /^UUID \(v(\w)\)$/.exec(text);
  if (uuid) return tr('searchPage.cls.uuid', { version: uuid[1] });
  const trace = /^Trace ID (\w+)$/.exec(text);
  if (trace) return tr('searchPage.cls.traceparentDetail', { id: trace[1] });
  const minted = /^minted (.+ UTC) · entity, request, correlation, workflow or registry-transaction ID$/.exec(text);
  if (minted) return tr('searchPage.cls.uuidMinted', { at: minted[1] });
  return text;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function CorrelationHint({ query }: { query: string }) {
  const { t } = useTranslation('domains');
  if (!UUID_RE.test(query)) return null;
  const [before, after] = t('search.correlation', { link: '\u0000' }).split('\u0000');
  return (
    <p className="border-t px-5 py-4 text-sm text-muted-foreground">
      {before}
      <Link to={correlationLink(query.toLowerCase())} className="font-medium text-primary hover:underline">
        {t('search.correlationLink')}
      </Link>
      {after}
    </p>
  );
}

function Results({ query }: { query: string }) {
  const { t } = useTranslation('domains');
  const result = useApi((signal) => api.search(query, { signal }), [query]);
  if (result.status === 'loading') {
    return (
      <div className="px-5">
        <Loading label={t('search.searching')} rows={3} />
      </div>
    );
  }
  if (result.status === 'error') {
    return (
      <div className="px-5 pb-5">
        <DataError error={result.error} onRetry={result.reload} title={t('search.failed')} />
      </div>
    );
  }
  const hits = result.data.results;
  if (hits.length === 0) {
    const [before, after] = t('search.noMatchesBody', { q: '\u0000' }).split('\u0000');
    return (
      <>
        <div className="px-5 pb-5">
          <EmptyState title={t('search.noMatches')} icon={SearchX}>
            {before}
            <code className="rounded bg-muted px-1 break-all">{query}</code>
            {after}
          </EmptyState>
        </div>
        <CorrelationHint query={query} />
      </>
    );
  }
  return (
    <>
      <ul className="divide-y border-t">
        {hits.map((hit, i) => {
          const target = hitTarget(hit, t);
          const body = (
            <>
              <Badge tone="neutral" icon={false} className="min-w-24 justify-center" title={hit.kind}>
                {t(`search.kinds.${hit.kind as SearchHitKind}`, { defaultValue: hit.kind })}
              </Badge>
              <span className="flex min-w-0 flex-1 flex-col">
                <strong className={target.mono ? 'font-mono text-sm font-semibold break-all' : 'font-semibold break-words'}>{target.title}</strong>
                {target.subtitle ? <span className="text-xs break-words text-muted-foreground">{target.subtitle}</span> : null}
              </span>
            </>
          );
          return (
            <li key={`${hit.kind}:${hit.id}:${i}`}>
              {target.to ? (
                <Link to={target.to} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none">
                  {body}
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </Link>
              ) : (
                <div className="flex items-center gap-3 px-5 py-3">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
      <CorrelationHint query={query} />
    </>
  );
}

function Candidate({ c }: { c: QueryCandidate }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-sm font-semibold">{clsText(c.label, t)}</strong>
        <Badge tone={CONFIDENCE_TONE[c.confidence]} icon={false}>
          {t(`searchPage.confidence.${c.confidence}`)}
        </Badge>
      </div>
      <div className="text-sm break-words text-muted-foreground">{clsText(c.detail, t)}</div>
      {c.actions.length ? (
        <div className="flex flex-wrap gap-2">
          {c.actions.map((a) => (
            <Button key={a.to} asChild variant="outline" size="sm">
              <Link to={a.to}>
                {clsText(a.label, t)} <ArrowRight />
              </Link>
            </Button>
          ))}
        </div>
      ) : null}
    </li>
  );
}

export function SearchPage() {
  const { t } = useTranslation('domains');
  const [params] = useSearchParams();
  const query = (params.get('q') ?? '').trim();
  useDocumentTitle(query ? t('search.docTitle', { q: query }) : t('search.title'));
  const { candidates } = classifyQuery(query);
  const [shortcutBefore, shortcutAfter] = t('search.shortcut', { key: '\u0000' }).split('\u0000');

  return (
    <>
      <PageHeader
        title={t('search.title')}
        subtitle={query ? <code className="rounded bg-muted px-1.5 py-0.5 break-all text-foreground">{query}</code> : t('search.subtitle')}
      />
      {query ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Section flush title={t('search.results')} className="self-start lg:order-1">
            <Results query={query} />
          </Section>
          <Section
            title={t('search.interpreted')}
            description={t('search.interpretedHint')}
            className="self-start lg:order-2"
          >
            <ul className="divide-y">
              {candidates.map((c) => (
                <Candidate key={`${c.kind}-${c.label}`} c={c} />
              ))}
            </ul>
          </Section>
        </div>
      ) : (
        <Section title={t('search.accepts')} description={t('search.empty')}>
          <div className="flex gap-4">
            <div className="hidden size-10 shrink-0 items-center justify-center rounded-full bg-muted sm:flex">
              <Search className="size-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="flex flex-col gap-3 text-sm">
              <ul className="list-disc space-y-1 pl-5">
                {(['domains', 'names', 'contacts', 'trids', 'ids'] as const).map((k) => (
                  <li key={k}>{t(`search.acceptsList.${k}`)}</li>
                ))}
              </ul>
              <p className="text-muted-foreground">
                {shortcutBefore}
                <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">/</kbd>
                {shortcutAfter}
              </p>
            </div>
          </div>
        </Section>
      )}
    </>
  );
}
